/** Room data (shared string key → string value store): client-side checks mirroring the server limits. */

export const MAX_KEYS = 256
export const MAX_KEY_CHARS = 256
export const MAX_VALUE_CHARS = 500_000
export const MAX_TOTAL_VALUE_CHARS = 2_000_000

export type RoomData = Record<string, string>

export type EntryIssue =
  | "keyRequired"
  | "keyTooLong"
  | "keyExists"
  | "valueTooLong"
  | "tooManyKeys"
  | "totalTooLarge"

/** Characters as the server counts them (Unicode scalar values, not UTF-16 units). */
const chars = (s: string) => Array.from(s).length

/**
 * The room data after adding or editing one entry (`original` is the key being edited; renaming
 * moves the value). Keys are trimmed: the server rejects surrounding whitespace.
 */
export function withEntry(
  data: RoomData,
  entry: { key: string; value: string; original?: string },
): RoomData {
  const next = { ...data }
  if (entry.original != null) delete next[entry.original]
  next[entry.key.trim()] = entry.value
  return next
}

/** The first problem with saving `entry` into `data`, or `null`. */
export function entryIssue(
  data: RoomData,
  entry: { key: string; value: string; original?: string },
): EntryIssue | null {
  const key = entry.key.trim()
  if (!key) return "keyRequired"
  if (chars(key) > MAX_KEY_CHARS) return "keyTooLong"
  if (key !== entry.original && Object.hasOwn(data, key)) return "keyExists"
  if (chars(entry.value) > MAX_VALUE_CHARS) return "valueTooLong"
  const next = withEntry(data, entry)
  if (Object.keys(next).length > MAX_KEYS) return "tooManyKeys"
  if (Object.values(next).reduce((n, v) => n + chars(v), 0) > MAX_TOTAL_VALUE_CHARS) return "totalTooLarge"
  return null
}
